import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, merge, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { TextField as FluentTextField } from 'office-ui-fabric-react/lib/TextField';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import TextButton from '../TextButton';
import StackGrid from '../StackGrid';
import { createProduct, editProduct, fetchProduct } from '../../actions/products';
import { fetchPartner } from '../../actions/partners';
import TextField from '../TextField';
import Dropdown from '../Dropdown';
import styles from './ProductForm.style';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import SubModuleContent from '../SubModuleContent';
import DetailsList from '../DetailsList';
import { silos } from '../../constants';
import FormikTypeAheadV2 from '../TypeaheadPicker/FormikTypeaheadPicker.v2';
import * as api from '../../utils/api';

const ProductSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  owner: Yup.string().required('Required'),
  tokenId: Yup.string(),

  metadata: Yup.object().shape({
    silo: Yup.string().required('Required'),
    productIdsWithPfam: Yup.array(
      Yup.object().shape({
        productId: Yup.string().required('Required'),
        pfamCode: Yup.string(),
      }),
    ).when('silo', (silo, schema) => {
      return silo === '3p-tla' ? schema.required('Required') : schema;
    }),
  }),
});

async function searchProducts(query) {
  const data = await api.get('/search/products/1p-azure', {
    params: {
      q: query,
    },
  });

  return data.products.map(productName => ({
    key: productName,
    text: productName,
  }));
}

async function searchPartners(query) {
  const data = await api.get('/search', {
    params: {
      q: query,
      'resources[]': 'partners',
    },
  });

  return (data.partners || []).map(partner => ({
    key: partner.partnerAddress,
    text: partner.name,
  }));
}

const ProductForm = React.forwardRef(({ tokenId, onSuccess }, ref) => {
  const dispatch = useDispatch();
  const [showProductIdentifier, setShowProductIdentifier] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const product = useSelector(state => get(state, `entities.products.${tokenId}`, {}));
  const partner = useSelector(state => get(state, `entities.partners.${product?.owner}`, ''));

  useEffect(() => {
    const loadData = async () => {
      await dispatch(fetchProduct(tokenId));
      setIsLoading(false);
    };
    if (tokenId && isEmpty(product)) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [dispatch, tokenId]);

  useEffect(() => {
    if (product?.owner) {
      dispatch(fetchPartner(product.owner));
    }
  }, [dispatch, product]);

  const initialValues = merge(
    {
      name: '',
      owner: '',
      metadata: {
        productIdsWithPfam: [],
        silo: '',
      },
    },
    product,
    { tokenId: undefined },
  );

  if (isLoading) {
    return (
      <Spinner
        size={SpinnerSize.medium}
        className={styles.loadingSpinner}
        data-testid="roleSpinner"
      />
    );
  }

  return (
    <Formik
      innerRef={ref}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize
      validationSchema={ProductSchema}
      onSubmit={async (submittedValues, { setErrors }) => {
        let result;
        if (tokenId) {
          result = await dispatch(editProduct(tokenId, submittedValues));
        } else {
          result = await dispatch(createProduct(submittedValues));
        }

        if (result.error && result.payload.status === 409) {
          setErrors({ form: `Product with name ${submittedValues.name} already exists` });
        } else if (result.error && result.payload.message) {
          setErrors({ form: result.payload.message });
        }

        // if no errors and
        if (!result.error) {
          onSuccess(get(result, `payload.entities.products.${result.payload.result}`));
        }
        return result;
      }}
    >
      {({ values, isSubmitting, errors, setFieldValue }) => {
        const [newProduct, setNewProduct] = useState(null);

        const handleProductIdSave = () => {
          if (
            newProduct?.productId &&
            newProduct?.pfamCode &&
            (values?.metadata?.productIdsWithPfam[editingIndex] ||
              !(values?.metadata?.productIdsWithPfam).find(
                prodId => prodId.productId === newProduct.productId,
              ))
          ) {
            setFieldValue(`metadata.productIdsWithPfam.${editingIndex}`, {
              productId: newProduct.productId,
              pfamCode: newProduct.pfamCode,
            });
          }
          setNewProduct(null);
          setShowProductIdentifier(false);
        };

        const handleEdit = index => {
          setShowProductIdentifier(true);
          setEditingIndex(index);
          setNewProduct(values?.metadata?.productIdsWithPfam[index]);
        };

        const columns = ({ remove }) => [
          {
            key: 'productId',
            name: 'Product IDs',
            fieldName: 'productId',
            isResizable: false,
            minWidth: 300,
            maxWidth: 350,
          },
          {
            key: 'pfamCode',
            name: 'PFAM Code',
            fieldName: 'pfamCode',
            isResizable: false,
            minWidth: 300,
            maxWidth: 350,
          },
          {
            key: 'remove',
            name: 'Remove',
            arianame: 'remove',
            isIconOnly: true,
            fieldName: 'remove',
            headerClassName: styles.iconColumnHeader,
            minWidth: 25,
            maxWidth: 35,
            isPadded: true,
            onRender: (item, index) => {
              return (
                <TextButton
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    remove(index);
                  }}
                >
                  Remove
                </TextButton>
              );
            },
          },
          {
            key: 'edit',
            name: 'Edit',
            arianame: 'edit',
            isIconOnly: true,
            fieldName: 'edit',
            minWidth: 25,
            maxWidth: 35,
            isPadded: true,
            onRender: (item, index) => {
              return (
                <TextButton
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleEdit(index);
                  }}
                >
                  Edit
                </TextButton>
              );
            },
          },
        ];

        return (
          <Form noValidate>
            {errors.form && (
              <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline
                dismissButtonAriaLabel="Close"
                nowrap
              >
                {errors.form}
              </MessageBar>
            )}
            <SubModule disableAccordian>
              <SubModuleHeader>General Information</SubModuleHeader>
              <SubModuleContent>
                <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                  <Field
                    name="metadata.silo"
                    label="Silo"
                    options={silos}
                    component={Dropdown}
                    disabled={isSubmitting}
                    required
                  />

                  {values.metadata?.silo === '1p-azure' && !tokenId ? (
                    <Field
                      name="name"
                      label="Product Name"
                      placeholder="Search Azure Products"
                      component={FormikTypeAheadV2}
                      disabled={isSubmitting}
                      required
                      loadOptions={searchProducts}
                    />
                  ) : (
                    <Field
                      name="name"
                      label="Product Name"
                      component={TextField}
                      disabled={isSubmitting}
                      required
                    />
                  )}

                  {tokenId ? (
                    <Field
                      name="owner"
                      label="Partner Name"
                      placeholder="Partner Name"
                      value={partner.name}
                      component={TextField}
                      disabled
                    />
                  ) : (
                    <Field
                      name="owner"
                      label="Partner Name"
                      placeholder="Partner Name"
                      component={FormikTypeAheadV2}
                      disabled={isSubmitting}
                      loadOptions={searchPartners}
                      required
                    />
                  )}
                  {tokenId && (
                    <Field
                      name="tokenId"
                      label="Token ID"
                      disabled={tokenId !== null}
                      component={TextField}
                    />
                  )}
                </StackGrid>
              </SubModuleContent>
            </SubModule>

            <SubModule disableAccordian>
              <SubModuleHeader>Product Identifiers</SubModuleHeader>
              <SubModuleContent>
                {values.metadata.productIdsWithPfam.length === 0 &&
                get(errors, 'metadata.productIdsWithPfam') ? (
                  <MessageBar
                    messageBarType={MessageBarType.error}
                    dismissButtonAriaLabel="Close"
                    nowrap
                  >
                    {errors?.metadata?.productIdsWithPfam}
                  </MessageBar>
                ) : (
                  ''
                )}
                <FieldArray name="metadata.productIdsWithPfam">
                  {({ remove }) => (
                    <>
                      {values.metadata.productIdsWithPfam.length > 0 && (
                        <DetailsList
                          items={values.metadata.productIdsWithPfam.map(productIdWithPfam => ({
                            ...productIdWithPfam,
                            key: productIdWithPfam.productId,
                          }))}
                          columns={columns({ remove })}
                        />
                      )}
                      {showProductIdentifier && (
                        <SubModule disableAccordian>
                          <div className={styles.root}>
                            <StackGrid smCols={3} mdCols={3} lgCols={3} shrink>
                              <FluentTextField
                                name="newProductId"
                                label="Product Identifier"
                                value={newProduct?.productId || ''}
                                onChange={e =>
                                  setNewProduct(prevState => {
                                    return { ...prevState, productId: e.target.value };
                                  })
                                }
                                className={styles.productIdentifierField}
                                onGetErrorMessage={value =>
                                  newProduct && !value ? 'Required' : ''
                                }
                              />
                              <FluentTextField
                                name="newPFAMCode"
                                label="PFAM Code"
                                value={newProduct?.pfamCode || ''}
                                onChange={e =>
                                  setNewProduct(prevState => {
                                    return { ...prevState, pfamCode: e.target.value };
                                  })
                                }
                                className={styles.productIdentifierField}
                              />
                              <IconButton
                                iconProps={{ iconName: 'Cancel' }}
                                title="Close"
                                ariaLabel="Close"
                                className={styles.toggleCloseButton}
                                onClick={() => {
                                  setNewProduct(null);
                                  setShowProductIdentifier(false);
                                }}
                              />

                              <TextButton
                                type="button"
                                data-testid="save-productid-button"
                                onClick={handleProductIdSave}
                                disabled={
                                  newProduct && (!newProduct.productId || !newProduct.pfamCode)
                                }
                                className={styles.saveProductIdButton}
                              >
                                Save Product ID
                              </TextButton>
                            </StackGrid>
                          </div>
                        </SubModule>
                      )}
                    </>
                  )}
                </FieldArray>

                <TextButton
                  type="button"
                  disabled={showProductIdentifier}
                  className={styles.productAddButton}
                  onClick={() => {
                    setShowProductIdentifier(true);
                    setEditingIndex(values.metadata.productIdsWithPfam.length);
                  }}
                  data-testid="add-productid"
                >
                  Add a Product ID
                </TextButton>
              </SubModuleContent>
            </SubModule>
          </Form>
        );
      }}
    </Formik>
  );
});

ProductForm.propTypes = {
  tokenId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSuccess: PropTypes.func,
};

ProductForm.defaultProps = {
  tokenId: null,
  onSuccess: () => {},
};

export default ProductForm;
